import * as React from "react";
import { dataProvider } from "@/providers/dataProvider";
import type { AdminRole } from "@/providers/types";
import { RoleSelector } from "@/components/RoleSelector";
import { InlineError } from "@/components/InlineError";
import { Toast } from "@/components/Toast";

interface AddOperatorFormProps {
  onSuccess: () => void;
}

export function AddOperatorForm({ onSuccess }: AddOperatorFormProps): React.JSX.Element {
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<AdminRole>("support");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showToast, setShowToast] = React.useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!email.trim()) {
      setError("Введите email оператора");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await dataProvider.create({ resource: "operators", variables: { email: email.trim(), role } });
      setEmail("");
      setRole("support");
      setShowToast(true);
      onSuccess();
    } catch (err: unknown) {
      const bffErr = err as { code?: string; message?: string } | null;
      setError(bffErr?.message ?? "Ошибка создания оператора");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="px-6 pb-5">
      <form
        onSubmit={(e) => { void handleSubmit(e); }}
        className="flex flex-col gap-3 bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)] p-4"
        aria-label="Добавить оператора"
      >
        <p className="text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px]">
          Добавить оператора
        </p>

        <div className="flex gap-3 flex-wrap items-end">
          <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <label
              htmlFor="add-operator-email"
              className="text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px]"
            >
              Email
            </label>
            <input
              id="add-operator-email"
              type="email"
              placeholder="email оператора"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              className="h-10 px-3 border border-[var(--border)] rounded-[var(--radius-sm)] text-[14px] text-[var(--fg)] bg-[var(--surface)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-weak)] disabled:opacity-50"
              autoComplete="email"
            />
          </div>

          <div className="flex flex-col gap-1 w-[180px]">
            <label
              htmlFor="add-operator-role"
              className="text-[12px] font-[600] text-[var(--muted)] uppercase tracking-[0.4px]"
            >
              Роль
            </label>
            <RoleSelector
              id="add-operator-role"
              value={role}
              onChange={setRole}
              disabled={isSubmitting}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !email.trim()}
            className="h-10 px-5 bg-[var(--accent)] text-white text-[13px] font-[600] rounded-[var(--radius-sm)] hover:bg-[#155bd4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]"
          >
            {isSubmitting ? "Добавление..." : "Добавить"}
          </button>
        </div>

        {error && <InlineError message={error} />}
      </form>

      {showToast && (
        <div className="fixed bottom-6 right-6 z-50">
          <Toast
            message="Оператор добавлен"
            variant="success"
            onClose={() => setShowToast(false)}
          />
        </div>
      )}
    </div>
  );
}
